import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { useAuth } from '../stores/auth';
import { Logo, LotusMark } from '../components/site/Logo';
import { INTERIORS } from '../lib/site';

/**
 * The single funnel. Every stakeholder — CEO, property manager, technician, resident,
 * vendor — signs in here. What they see afterwards is decided entirely by their
 * permissions, not by which app they opened.
 */
const DEMO_LOGINS = [
  { label: 'Chief Executive', email: 'rajiv@lotusliving.co.uk', hint: 'Rajiv Nehru · full access' },
  { label: 'Property Manager', email: 'pm@lotusliving.co.uk', hint: 'Portfolio & maintenance' },
  { label: 'Lettings', email: 'lettings@lotusliving.co.uk', hint: 'Leads, viewings, tenancies' },
  { label: 'Finance', email: 'finance@lotusliving.co.uk', hint: 'Ledger, invoices, reports' },
  { label: 'Maintenance', email: 'tech@lotusliving.co.uk', hint: 'Own assigned jobs only' },
];

export default function LoginPage() {
  const { user, login, loading, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('Lotus2026!');
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/app" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try { await login(email, password); navigate('/app'); }
    catch { /* The store surfaces the message. */ }
    finally { setSubmitting(false); }
  }

  return (
    <div className="grid min-h-screen bg-white font-sans lg:grid-cols-[1.1fr_1fr]">
      {/* Photograph */}
      <div className="relative hidden overflow-hidden bg-ink-900 lg:block">
        <img src={INTERIORS.livingArea} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/85 via-ink-900/25 to-ink-900/30" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 xl:p-16">
          <Logo tone="white" className="h-10" />
          <div className="max-w-lg">
            <p className="eyebrow !text-bronze-300">One platform, every role</p>
            <h1 className="display mt-4 text-[48px] leading-[1.05] text-white xl:text-[60px]">Land to lease-up <em>to living</em>.</h1>
            <p className="mt-5 text-[15px] leading-relaxed text-white/75">Properties, tenancies, maintenance and the ledger in one system across the UK and UAE — with an assistant that only ever sees what you're allowed to.</p>
          </div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">Build-to-rent, end to end</p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          <div className="mb-10 flex items-center justify-between lg:hidden">
            <Logo className="h-9" />
          </div>
          <LotusMark className="hidden h-8 w-8 lg:block" />
          <h2 className="display mt-6 text-[38px] text-ink-900">Sign in</h2>
          <p className="mt-2 text-[14px] text-ink-500">One account. Your view is set by your role, not by which app you opened.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-[12px] font-medium text-ink-600">Email</span>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@lotusliving.co.uk" className="field" autoComplete="username" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[12px] font-medium text-ink-600">Password</span>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="field" autoComplete="current-password" />
            </label>
            {error && <p className="rounded-xl border border-red-100 bg-red-50 px-3.5 py-2.5 text-[13px] text-red-700">{error}</p>}
            <button type="submit" disabled={submitting || loading} className="group flex w-full items-center justify-between rounded-full border border-ink-900 bg-ink-900 px-6 py-3.5 text-[13px] font-medium text-white transition-all duration-500 hover:bg-white hover:text-ink-900 disabled:opacity-50">
              <span>{submitting ? 'Signing in…' : 'Sign in'}</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </form>

          <div className="mt-10">
            <div className="mb-3 flex items-center gap-3">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.2em] text-ink-400">Demo accounts</span>
              <span className="h-px flex-1 bg-ink-200" />
              <span className="text-[10.5px] text-ink-400">password Lotus2026!</span>
            </div>
            <div className="divide-y divide-ink-100 overflow-hidden rounded-2xl border border-ink-200">
              {DEMO_LOGINS.map((d) => (
                <button key={d.email} type="button" onClick={() => setEmail(d.email)} className={`flex w-full items-center justify-between px-4 py-3 text-left transition hover:bg-bronze-50 ${email === d.email ? 'bg-bronze-50' : 'bg-white'}`}>
                  <span>
                    <span className="block text-[13px] font-medium text-ink-900">{d.label}</span>
                    <span className="block text-[11.5px] text-ink-500">{d.hint}</span>
                  </span>
                  <span className={`h-2 w-2 rounded-full ${email === d.email ? 'bg-bronze-500' : 'bg-ink-200'}`} />
                </button>
              ))}
            </div>
            <p className="mt-3 text-[11.5px] text-ink-400">Residents: use the email on your tenancy. Everything you see is scoped to your own home.</p>
          </div>

          <Link to="/" className="mt-10 inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-500 transition hover:text-ink-900"><ArrowLeft className="h-3.5 w-3.5" /> Back to lotusliving.co.uk</Link>
        </div>
      </div>
    </div>
  );
}
